import { differenceInMinutes } from "date-fns";
import { ArrowRightIcon, CircleAlert, Clock, ExternalLinkIcon, Plane, StepForwardIcon } from "lucide-react";
import { DateTime } from "luxon";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  type AiFlightEntry,
  arrivalReliability,
  formatDeltaMinutes,
  SANE_DELAY_CAP_MIN,
} from "../../lib/incident-delta";

/**
 * Pure presentational card for one AI-extracted flight (original or actual
 * itinerary). Port of AiIncidentFlightCard.vue.
 */

// Tolerant parse: scheduled times are ISO ("2025-10-13T06:05:00") but actual
// times come space-separated ("2025-10-13 06:08"), which fromISO rejects.
function toDateTime(value: string | null | undefined) {
  if (!value) {
    return null;
  }
  let dt = DateTime.fromISO(value);
  if (!dt.isValid) {
    dt = DateTime.fromSQL(value);
  }
  if (!dt.isValid) {
    dt = DateTime.fromJSDate(new Date(value));
  }
  return dt.isValid ? dt : null;
}

// Format time (HH:mm)
function formatTime(dateString: string | null | undefined) {
  return toDateTime(dateString)?.toFormat("HH:mm") ?? "—";
}

// Format date (d LLL yyyy)
function formatDate(dateString: string | null | undefined) {
  return toDateTime(dateString)?.setLocale("fr").toFormat("d LLL yyyy") ?? "—";
}

// Calculate duration between two times
function calculateDuration(departure: string | null | undefined, arrival: string | null | undefined) {
  if (!departure || !arrival) {
    return "—";
  }

  const dep = new Date(departure);
  const arr = new Date(arrival);
  const diff = arr.getTime() - dep.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours}h ${minutes}m`;
}

// Format delay duration
function formatDelayDuration(minutes: number) {
  const hours = Math.floor(Math.abs(minutes) / 60);
  const mins = Math.abs(minutes) % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

interface AiIncidentFlightCardProps {
  flight: AiFlightEntry;
  type: "original" | "actual";
  isLast?: boolean;
  isFirst?: boolean;
  /** Next flight in sequence for correspondence time calculation. */
  nextFlight?: AiFlightEntry;
}

function AirportTooltip({ code, name, city }: { code?: string; name?: string | null; city?: string | null }) {
  return (
    <TooltipProvider delay={0}>
      <Tooltip>
        <TooltipTrigger className="cursor-help text-lg font-bold text-slate-800">{code}</TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <div className="font-semibold">{name || code}</div>
            {city ? <div className="text-xs text-slate-400">{city}</div> : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function AiIncidentFlightCard({
  flight,
  type,
  isLast = false,
  isFirst = false,
  nextFlight,
}: AiIncidentFlightCardProps) {
  const flightData = flight?.flight;
  const segment = flight?.segment;

  // Check if departure is delayed (>30 minutes)
  const isDelayedDeparture =
    !!flightData?.actualDeparture &&
    !!flightData?.scheduledDeparture &&
    Math.abs(
      differenceInMinutes(new Date(flightData.actualDeparture), new Date(flightData.scheduledDeparture)),
    ) > 30;

  // Check if arrival is delayed (>30 minutes)
  const isDelayedArrival =
    !!flightData?.actualArrival &&
    !!flightData?.scheduledArrival &&
    Math.abs(differenceInMinutes(new Date(flightData.actualArrival), new Date(flightData.scheduledArrival))) >
      30;

  // Guard against a corrupt actualArrival from the assessment pipeline (shared, tested helper):
  // some insights carry an arrival whose date is days off, which would otherwise show as
  // "Retard de 143h" / "Correspondance −142h". See arrivalReliability (TECH-705).
  const {
    delayMinutes,
    reliable: actualArrivalReliable,
    reliableActualArrival,
  } = arrivalReliability(flightData);

  const isCancelled = flightData?.monitoringStatus === "CANCELLED";

  // Get delta from flight data
  const departureDelta = flight?.delta?.departure ?? null;
  const arrivalDelta = flight?.delta?.arrival ?? null;

  // Show delta only when it carries information:
  // - Last flight: a non-zero final destination arrival delay (capped, so a corrupt actualArrival can't
  //   show 143h; and 0 is hidden — an on-time reroute after a cancellation shouldn't read as a delay)
  // - First flight: departure delta if advanced > 1h
  const hasDelta =
    (isLast && arrivalDelta !== null && arrivalDelta !== 0 && Math.abs(arrivalDelta) <= SANE_DELAY_CAP_MIN) ||
    (isFirst && departureDelta !== null && departureDelta < -60);

  // Low connection time: < 15min between arrival and next flight's scheduled departure.
  // Uses the reliable arrival, so a corrupt actualArrival can't fake a tight/negative connection.
  const arrivalForConnection = reliableActualArrival;
  const nextScheduledDeparture = nextFlight?.flight?.scheduledDeparture;
  const isLowConnectionTime =
    !!arrivalForConnection &&
    !!nextScheduledDeparture &&
    (new Date(nextScheduledDeparture).getTime() - new Date(arrivalForConnection).getTime()) / (1000 * 60) <
      15;

  // Calculate correspondence time between this flight and the next
  let correspondenceTime: string | null = null;
  {
    let arrivalTime: string | null | undefined;
    let nextDepartureTime: string | null | undefined;

    if (isLowConnectionTime) {
      arrivalTime = reliableActualArrival;
      nextDepartureTime = nextFlight?.flight?.actualDeparture || nextFlight?.flight?.scheduledDeparture;
    } else {
      arrivalTime = reliableActualArrival || flightData?.scheduledArrival;
      nextDepartureTime = nextFlight?.flight?.actualDeparture || nextFlight?.flight?.scheduledDeparture;
    }

    if (arrivalTime && nextDepartureTime) {
      const diffMinutes =
        (new Date(nextDepartureTime).getTime() - new Date(arrivalTime).getTime()) / (1000 * 60);
      // Suppress implausible layovers (corrupt next-flight timestamp) rather than show "142h".
      if ((isLowConnectionTime || diffMinutes >= 0) && Math.abs(diffMinutes) <= SANE_DELAY_CAP_MIN) {
        const absDiff = Math.abs(diffMinutes);
        const hours = Math.floor(absDiff / 60);
        const minutes = Math.floor(absDiff % 60);
        const formatted = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        correspondenceTime = diffMinutes < 0 ? `-${formatted}` : formatted;
      }
    }
  }

  // The layover is only ever unknown when this leg's real arrival is corrupt — say "à vérifier"
  // rather than leave the connection blank (which reads as if it were removed).
  const correspondenceSuspect = !correspondenceTime && !!flightData?.actualArrival && !actualArrivalReliable;

  const cancellationDateTitle = segment?.cancellationDate
    ? new Date(segment.cancellationDate).toLocaleDateString()
    : "";

  return (
    <div className="relative">
      <div className="p-6 transition-colors duration-200">
        {/* Flight Route Header */}
        <div
          className={cn(
            "flex items-center pb-4 text-left text-base font-bold leading-8",
            type === "original" ? "text-blue-700" : "text-amber-700",
          )}
        >
          <h2 className="inline-flex items-center">
            <a
              className="inline-flex items-center hover:underline"
              href={`https://www.flightradar24.com/data/flights/${flightData?.flightIdentifier?.toLowerCase()}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <span>Vol {flightData?.flightIdentifier}</span>
              <ExternalLinkIcon className="mx-2 h-4 w-4 text-gray-500" />
            </a>
            {/* Cancellation badges */}
            {segment?.cancellationType === "SEGMENT" ? (
              <span
                title={cancellationDateTitle}
                className="ml-2 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"
              >
                <svg className="size-1.5 fill-red-500" viewBox="0 0 6 6" aria-hidden="true">
                  <circle cx="3" cy="3" r="3" />
                </svg>
                HX
              </span>
            ) : segment?.cancellationType === "RECORD" ? (
              <span
                title={cancellationDateTitle}
                className="ml-2 inline-flex items-center gap-x-1.5 rounded-md bg-zinc-500 px-2 py-1 text-xs font-medium text-gray-100 ring-1 ring-inset ring-zinc-400"
              >
                <svg className="size-1.5 fill-white" viewBox="0 0 6 6" aria-hidden="true">
                  <circle cx="3" cy="3" r="3" />
                </svg>
                Cancel
              </span>
            ) : null}
          </h2>
        </div>

        {/* Airport Route Display */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <AirportTooltip
                code={flightData?.departureAirport}
                name={flightData?.departureAirportName}
                city={flightData?.departureAirportCity}
              />
              <div className="flex items-center gap-2">
                <div className="h-px w-12 bg-slate-300" />
                <Plane className="h-4 w-4 rotate-45 transform text-slate-400" />
                <div className="h-px w-12 bg-slate-300" />
              </div>
              <AirportTooltip
                code={flightData?.arrivalAirport}
                name={flightData?.arrivalAirportName}
                city={flightData?.arrivalAirportCity}
              />
            </div>
          </div>
        </div>

        {/* Flight Times */}
        <div className="grid grid-cols-2 gap-6">
          {/* Departure Time */}
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Départ</div>

            {isCancelled || isDelayedDeparture ? (
              <div className="inline-flex items-center text-lg font-semibold tracking-tight text-slate-800">
                <span className="line-through">{formatTime(flightData?.scheduledDeparture)}</span>
                <StepForwardIcon className="mx-3 h-5 w-5 text-slate-500" />
                {flightData?.actualDeparture ? (
                  <span className="text-amber-600">{formatTime(flightData.actualDeparture)}</span>
                ) : isCancelled ? (
                  <span className="lowercase tracking-tighter text-red-800">ANNULÉ</span>
                ) : (
                  <span className="lowercase tracking-tighter text-stone-800">Aucune donnée</span>
                )}
              </div>
            ) : (
              <div className="text-lg font-semibold tracking-tight text-slate-800">
                {formatTime(flightData?.scheduledDeparture)}
              </div>
            )}

            <div className="text-sm text-slate-500">{formatDate(flightData?.scheduledDeparture)}</div>
          </div>

          {/* Arrival Time */}
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Arrivée</div>

            {isCancelled || isDelayedArrival ? (
              <div className="inline-flex items-center text-lg font-semibold tracking-tight text-slate-800">
                <span className="line-through">{formatTime(flightData?.scheduledArrival)}</span>
                <StepForwardIcon className="mx-3 h-5 w-5 text-slate-500" />
                {flightData?.actualArrival ? (
                  <span className="text-amber-600">{formatTime(flightData.actualArrival)}</span>
                ) : isCancelled ? (
                  <span className="lowercase tracking-tighter text-red-800">ANNULÉ</span>
                ) : null}
              </div>
            ) : (
              <div className="text-lg font-semibold tracking-tight text-slate-800">
                {formatTime(flightData?.scheduledArrival)}
              </div>
            )}

            <div className="text-sm text-slate-500">{formatDate(flightData?.scheduledArrival)}</div>
          </div>
        </div>

        {/* Flight Duration & per-flight actual Delay — factual for any claim type; only the corrupt
            real arrival (>72h off) is held back behind "à vérifier" rather than shown as "143h" */}
        <div className="mt-3 grid grid-cols-2 gap-6 text-xs text-slate-500">
          <div>Durée: {calculateDuration(flightData?.scheduledDeparture, flightData?.scheduledArrival)}</div>
          {delayMinutes > 30 ? (
            <div className="italic text-amber-600">Retard de {formatDelayDuration(delayMinutes)}</div>
          ) : flightData?.actualArrival && !actualArrivalReliable ? (
            <div className="italic text-slate-400">Retard réel à vérifier</div>
          ) : null}
        </div>

        {/* Delta Display (for actual itinerary) — unsigned here; the +/- sign lives in the
            dedicated delta card, "(vol avancé)" already tells the direction */}
        {hasDelta ? (
          <div className="mt-3 text-xs text-red-800">
            {departureDelta !== null && departureDelta < -60 ? (
              <span>
                Delta départ:{" "}
                <span className="font-semibold tracking-tight text-indigo-800">
                  {formatDeltaMinutes(departureDelta)} (vol avancé)
                </span>
              </span>
            ) : (
              <span>
                Delta arrivée:{" "}
                <span className="font-semibold tracking-tight text-red-800">
                  {formatDeltaMinutes(arrivalDelta ?? 0)}
                </span>
              </span>
            )}
          </div>
        ) : null}

        {/* Operating Airline */}
        {flightData?.operatingAirline || flightData?.operatingAirlineName ? (
          <div className="mt-3 text-xs text-indigo-500">
            Opéré par:{" "}
            <span className="font-semibold">
              {flightData?.operatingAirlineName ||
                (typeof flightData?.operatingAirline === "string"
                  ? flightData.operatingAirline
                  : flightData?.operatingAirline?.name)}
            </span>
          </div>
        ) : null}

        {/* Arrow Right Icon (only for original) */}
        {type === "original" ? (
          <div className="absolute right-6 top-5 z-50 text-slate-500">
            <ArrowRightIcon className="h-12 w-12" />
          </div>
        ) : null}
      </div>

      {/* Connection Line (between flights in same sequence) */}
      {!isLast ? (
        <div className="flex justify-center bg-slate-50 py-2">
          <div
            className={cn(
              "flex items-center gap-2 rounded-full border px-3 py-1",
              isLowConnectionTime ? "border-red-300 bg-red-50" : "border-slate-200 bg-white",
            )}
          >
            {isLowConnectionTime ? (
              <CircleAlert className="h-3 w-3 text-red-500" />
            ) : (
              <Clock className="h-3 w-3 text-slate-400" />
            )}
            <span className={cn("text-xs", isLowConnectionTime ? "text-red-600" : "text-slate-500")}>
              Correspondance{" "}
              {correspondenceTime ? (
                <span className="font-semibold">{correspondenceTime}</span>
              ) : correspondenceSuspect ? (
                <span className="italic text-slate-400">à vérifier</span>
              ) : null}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
